import { html } from '../core/html.js';
import { t, getLanguage } from '../core/i18n.js';
import { routeHref } from '../core/url.js';
import { formatDate } from '../core/format.js';
import { page, breadcrumbs } from '../components/chrome.js';
import { artwork } from '../components/artwork.js';
import { ctaSection } from '../components/ctaSection.js';
import { icon } from '../components/icon.js';
import { articleBody, articleHeadings } from '../components/prose.js';
import { vizFigure } from '../components/viz.js';
import { site } from '../data/site.js';

const pad = (value) => String(value).padStart(2, '0');

/**
 * One article. The post module (figures) and the copy for the active locale
 * are separate chunks, so the body of a post is only fetched on its own page.
 */
export const BlogPostPage = async (post) => {
  const module = await post.load();
  const copy = (await module.loadCopy(getLanguage())).default;
  const title = t(`post.${post.slug}.title`);
  const headings = articleHeadings(copy.blocks);
  const figure = (id, number) => vizFigure(id, number, module.figures[id], copy.figures[id]);

  return page(html`
    <article class="post" style="--accent: ${post.accent}" aria-labelledby="post-title">
      <header class="pagehead post-head">
        <div class="shell">
          ${breadcrumbs(null, { parent: { path: '/blog', labelKey: 'nav.blog' }, label: title })}
          <p class="kicker">${t(post.topicKey)}</p>
          <h1 class="post-head__title" id="post-title">${title}</h1>
          <p class="pagehead__lede post-head__lede">${copy.lede}</p>
          <p class="post-head__meta">
            <span>${t('blog.byline', { author: site.director })}</span>
            <time datetime="${post.published}">${formatDate(post.published)}</time>
            <span>${t('blog.readingTime', { minutes: post.minutes })}</span>
          </p>
        </div>
      </header>

      <div class="shell post-cover">
        <div class="post-cover__frame media-frame">
          ${artwork(post.cover, copy.coverAlt, { sizes: '(max-width: 1280px) 100vw, 1200px', priority: true })}
        </div>
      </div>

      <div class="shell post__layout">
        <nav class="post-toc" aria-labelledby="post-toc-title">
          <p class="post-toc__title" id="post-toc-title">${t('blog.contents')}</p>
          <ol>
            ${headings.map(
              (heading, index) => html`
                <li>
                  <a href="#${heading.id}"><span class="post-toc__num">${pad(index + 1)}</span>${heading.text}</a>
                </li>
              `,
            )}
          </ol>
        </nav>

        <div class="post__body">
          ${articleBody(copy.blocks, figure)}
          <p class="post__back">
            <a class="link-back" href="${routeHref('/blog')}">${icon('arrowLeft', { size: 15 })}${t('blog.back')}</a>
          </p>
        </div>
      </div>
    </article>
    ${ctaSection()}
  `);
};
